import { Children, useState, useEffect, useDeferredValue } from "react";
import { AddIcon, TaskIcon } from "allIcons";
import {
  BodyContainer,
  HeaderContainer,
  InputField,
  ListCard,
  ListIconWrapper,
  MainButton,
  PrimaryHeading,
  RightMainContainer,
  TemplateNotFound,
  LoadingListCards,
  Loader,
  FiltersList,
} from "commonUI";
import { useMutation } from "@tanstack/react-query";
import { useSelector, useDispatch } from "react-redux";
import {
  addSingleTask,
  deleteSingleTask,
} from "redux/reducers/templateLibrary/tasks";
import { addTask, deleteTask, useTasks } from "api/templateLibrary/tasks";
import { Link } from "react-router-dom";
import { useNotification } from "notification";
import { useTags } from "api/siteSettings/Tags";
import { useUserPermissions, useUserRole } from "hooks/user";
import {
  libraryItemSorting,
  libraryItemFiltering,
} from "filterAndSort/templateLibrary";
import { t } from "i18next";
import { invalidateTemplatePhases } from "api/templateLibrary/phases";
import { isEqual } from "helpers";
import { ModulePrefixes } from "enums";

export default function Task() {
  const [filterData, setFilterData] = useState({});
  const notification = useNotification();
  const { userData } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const { data: tasks, isLoading } = useTasks();
  const { data: tags } = useTags();
  const [searchText, setSearchText] = useState("");
  const defferedSearch = useDeferredValue(searchText);
  const [searchResult, setSearchResult] = useState([]);
  const { isSuperAdmin } = useUserRole();
  const { add_new_templates, edit_templates, delete_templates } =
    useUserPermissions();

  useEffect(() => {
    setSearchResult(
      tasks.filter((task) =>
        task.title.toLowerCase().includes(defferedSearch.toLowerCase().trim())
      )
    );
  }, [defferedSearch, tasks]);
  const addMutation = useMutation(addTask, {
    onSuccess: (data) => {
      notification({
        type: "success",
        message: t("libraryTemplate.tasks.api.success.duplicated"),
      });
      dispatch(addSingleTask(data.data));
      dispatch(invalidateTemplatePhases());
    },
    onError: (error) => {
      console.log(error);
    },
  });
  const deleteMutation = useMutation(deleteTask, {
    onSuccess: (data) => {
      notification({
        type: "success",
        message: t("libraryTemplate.tasks.api.success.deleted"),
      });
      dispatch(deleteSingleTask(data.id));
      dispatch(invalidateTemplatePhases());
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const applySorting = (order, orderBy) => {
    setSearchResult((prevSearchResult) =>
      libraryItemSorting(order, orderBy, prevSearchResult)
    );
  };

  const applyFilter = ({
    selectedFilter,
    selectedTags,
    selectedLibrarySource,
    ...data
  }) => {
    setFilterData({
      ...data,
      selectedTags,
      selectedLibrarySource,
      selectedFilter,
    });
    setSearchResult(
      libraryItemFiltering(
        selectedFilter,
        selectedTags,
        selectedLibrarySource,
        tasks
      )
    );
  };

  return (
    <>
      <RightMainContainer>
        {(addMutation.isLoading || deleteMutation.isLoading) && (
          <Loader loader="block" />
        )}
        <HeaderContainer>
          <PrimaryHeading>{t("libraryTemplate.tasks.title")}</PrimaryHeading>
          {add_new_templates ? (
            <Link to={`/${ModulePrefixes.TEMPLATE_LIBRARY}/tasks/add`}>
              <MainButton>
                <AddIcon />
                {t("button.add")}
              </MainButton>
            </Link>
          ) : (
            ""
          )}
        </HeaderContainer>
        <BodyContainer>
          <InputField
            placeholder={t("input.search.placeholder")}
            searchIcon={true}
            filter={true}
            value={searchText}
            setValue={(val) => setSearchText(val)}
            tags={tags ?? []}
            onApplyFilter={applyFilter}
            applySorting={applySorting}
            filterData={filterData}
          />
          <FiltersList filterData={filterData} updateFilter={applyFilter} />
          {isLoading ? (
            <LoadingListCards />
          ) : tasks.length === 0 || searchResult.length === 0 ? (
            <TemplateNotFound
              label={t("libraryTemplate.tasks.title")}
              linkLabel={t("libraryTemplate.task")}
              link={add_new_templates ? `/${ModulePrefixes.TEMPLATE_LIBRARY}/tasks/add` : ""}
            />
          ) : (
            Children.toArray(
              searchResult.map((task, index) => {
                return (
                  <ListCard
                    data={task.title}
                    duplicate={add_new_templates}
                    editIcon={edit_templates}
                    secondaryData={
                      isEqual(task.system_status, 1) && !isSuperAdmin
                        ? t("module.sourceType.system")
                        : ""
                    }
                    link={
                      edit_templates ? `/${ModulePrefixes.TEMPLATE_LIBRARY}/tasks/${task.id}` : ""
                    }
                    strictLink={true}
                    onDuplicate={() => {
                      addMutation.mutate({
                        ...task,
                        description: task.message ?? task.description,
                        tags:
                          typeof task.tags != "object"
                            ? JSON.parse(task.tags)
                            : task.tags,
                        title: `Copy of ${task.title}`,
                        name: userData?.name,
                        system_status: isSuperAdmin ? "1" : "0",
                      });
                    }}
                    onDelete={() => {
                      if (!delete_templates) return;
                      if (isEqual(task.system_status, 1) && !isSuperAdmin)
                        return;
                      deleteMutation.mutate(task.id);
                    }}
                    deleteIcon={
                      delete_templates
                        ? isEqual(task.system_status, 1) && !isSuperAdmin
                        : true
                    }
                    icon={
                      <ListIconWrapper>
                        <TaskIcon />
                      </ListIconWrapper>
                    }
                    deleteTitle={t("libraryTemplate.delete.title")}
                    deleteButtonText={t("libraryTemplate.delete.button")}
                    deleteMessage={t("libraryTemplate.delete.message")}
                  />
                );
              })
            )
          )}
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
